import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { setToastRef } from "../utils/toastHelper";

export type ToastType = "success" | "err" | "info" | "warning";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

export interface ToastContextType {
  showToast: (message: string, type: ToastType) => void;
  success: (message: string) => void;
  err: (message: string) => void;
  info: (message: string) => void;
  warning: (message: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within ToastProvider");
  }
  return context;
};

const ToastItem: React.FC<{ toast: Toast; onRemove: (id: string) => void }> = ({
  toast,
  onRemove,
}) => {
  const [progress, setProgress] = useState(100);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    const duration = 4000;
    const interval = 40;
    const decrement = (interval / duration) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - decrement;
        if (newProgress <= 0) {
          clearInterval(timer);
          setIsLeaving(true);
          setTimeout(() => onRemove(toast.id), 300);
          return 0;
        }
        return newProgress;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [toast.id, onRemove]);

  const handleClose = () => {
    setIsLeaving(true);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const config = {
    success: {
      bg: "from-emerald-500 to-green-600",
      icon: CheckCircle,
      shadow: "shadow-emerald-500/30",
    },
    err: {
      bg: "from-rose-500 to-red-600",
      icon: AlertCircle,
      shadow: "shadow-rose-500/30",
    },
    info: {
      bg: "from-blue-500 to-indigo-600",
      icon: Info,
      shadow: "shadow-blue-500/30",
    },
    warning: {
      bg: "from-amber-500 to-orange-600",
      icon: AlertTriangle,
      shadow: "shadow-amber-500/30",
    },
  }[toast.type];

  const Icon = config.icon;

  return (
    <div className={`toast-item ${isLeaving ? "toast-leave" : ""}`}>
      <div
        className={`relative flex items-center gap-3 bg-gradient-to-r ${config.bg} text-white pl-4 pr-3 py-3 rounded-xl shadow-lg ${config.shadow} backdrop-blur-sm overflow-hidden min-w-[320px] max-w-[400px]`}
      >
        {/* Shimmer effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>

        {/* Icon with glow */}
        <div className="relative flex-shrink-0">
          <div className="absolute inset-0 bg-white/20 rounded-lg animate-ping opacity-75"></div>
          <div className="relative bg-white/20 p-1.5 rounded-lg">
            <Icon className="w-5 h-5" strokeWidth={2.5} />
          </div>
        </div>

        {/* Message */}
        <p className="flex-1 text-sm font-semibold pr-2">{toast.message}</p>

        {/* Close button */}
        <button
          onClick={handleClose}
          className="flex-shrink-0 hover:bg-white/20 p-1 rounded-lg transition-all hover:rotate-90"
          aria-label="Close"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>

        {/* Progress bar */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
          <div
            className="h-full bg-white/50 transition-all duration-75 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // const showToast = useCallback((message: string, type: ToastType) => {
  //   const id = Math.random().toString(36).substring(2, 9);
  //   const newToast: Toast = { id, message, type };
  //   setToasts((prev) => [...prev, newToast]);
  // }, []);

  const showToast = useCallback((message: string, type: ToastType) => {
    setToasts((prev) => {
      // Check if the same message already exists
      const existingToast = prev.find(
        (toast) => toast.message === message && toast.type === type,
      );
      if (existingToast) {
        // Reset the progress of the existing toast by replacing it with a new object
        return prev.map((toast) =>
          toast.id === existingToast.id
            ? { ...toast, id: Math.random().toString(36).substring(2, 9) } // new id to restart timer
            : toast,
        );
      }

      // Otherwise, add a new toast
      const id = Math.random().toString(36).substring(2, 9);
      return [...prev, { id, message, type }];
    });
  }, []);

  const success = useCallback(
    (message: string) => showToast(message, "success"),
    [showToast],
  );
  const err = useCallback(
    (message: string) => showToast(message, "err"),
    [showToast],
  );
  const info = useCallback(
    (message: string) => showToast(message, "info"),
    [showToast],
  );
  const warning = useCallback(
    (message: string) => showToast(message, "warning"),
    [showToast],
  );

  // Set the global toast reference
  useEffect(() => {
    setToastRef({ showToast, success, err, info, warning });
  }, [showToast, success, err, info, warning]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, success, err, info, warning }}>
      {children}

      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateY(400px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes slideOut {
          from {
            transform: translateY(0);
            opacity: 1;
          }
          to {
            transform: translateY(400px);
            opacity: 0;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }

        .toast-item {
          animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .toast-leave {
          animation: slideOut 0.3s ease-in forwards;
        }

        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </ToastContext.Provider>
  );
};
